import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BaseService} from "../../services/base.service";
import {HttpClient} from "@angular/common/http";
import {Meta, Title} from "@angular/platform-browser";
import {finalize, takeUntil} from "rxjs/operators";
import {Subject, Subscription} from "rxjs";
import {Dels, Orders, OrdersData, ReturnStatuses} from "../../interfaces/orders-data";
import {Currencies} from "../../interfaces/currencies";
import {OrderProductsWars, ShowTrackStatus, Transfer} from "../../interfaces/show-track-status";
import * as moment from "moment";
import {OrdersInfo} from "../../interfaces/orders-history";
import {ModalSelectGoodsReturnComponent} from "./modal-select-goods-return/modal-select-goods-return.component";
import {
  ModalPartnersNotReturnOrdersMessageComponent
} from "./modal-partners-not-return-orders-message/modal-partners-not-return-orders-message.component";
import {AssistantActionsService} from "../../services/assistant-actions.service";
import {AssistantHighlight} from "../../shared/assistant-highlight";

/** Що просить показати помічник (#966): замовлення, яке підсвітити й (expand) розкрити, як клік по ньому */
interface AssistantReturnsShow {
  id: number;
  expand: boolean;
  // Пошук уже знято під це замовлення - вдруге список не перечитуємо
  cleared: boolean;
}

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss']
})
export class OrdersComponent extends BaseService implements OnDestroy, OnInit {
  @ViewChild(ModalSelectGoodsReturnComponent) modalSelectGoodsReturnComponentRef: ModalSelectGoodsReturnComponent;
  @ViewChild(ModalPartnersNotReturnOrdersMessageComponent) modalPartnersNotReturnOrdersMessageComponentRef: ModalPartnersNotReturnOrdersMessageComponent;

  public isDestroyed: Subject<boolean> = new Subject();
  public timer;
  // Попередній запит списку скасовуємо: інакше старша відповідь пошуку, що прийшла пізніше, перезаписувала новішу
  private dataSub: Subscription;
  public search = '';
  public delivery = '';
  public orders: Orders[];
  public currencies: Currencies[];
  public isDarkBackground = false;
  public isShowOrderModalShow = false;
  public date;
  public trackStatusOrderProductsWars: OrderProductsWars[];
  public trackStatusTransfer: Transfer[];
  public trackStatusContactsDrivers;
  public isTrackStatusModalShow = false;
  public isOrderModalShow = false;
  public trackStatusOrder: OrdersInfo;
  public isFormationRequestReturn = false;
  public returnStatuses: ReturnStatuses[];
  public dels: Dels[];
  public isModalUserInfo = false;
  public wrongComment = false;
  public wrongQuantityGood = false;
  public changeButton = false;
  public allow_to_return_goods_from_zakupolog: boolean;
  public currentYear: number;
  private selectedOrder: Orders;

  // Картка #966: замовлення, на яке вказав помічник; що він просить показати, поки список не готовий; номер запиту списку -
  // розкриття чекає відповіді на останній
  private assistantActions = inject(AssistantActionsService);
  public highlight = new AssistantHighlight(inject(ElementRef));
  private assistantShow: AssistantReturnsShow | null = null;
  private dataSeq = 0;
  private dataLoading = false;
  private assistantUnregister: () => void;

  constructor(
    private http: HttpClient,
    private titleService: Title,
    private meta: Meta,
  ) {
    super();

    const title = 'Маслоточка | Повернення';
    this.titleService.setTitle(title);
    this.currentYear = moment().year();
    this.meta.addTag({name: 'title', content: title});
    this.meta.addTag({name: 'og:title', content: title});
    this.meta.addTag({name: 'twitter:title', content: title});
    this.meta.addTag({name: 'DC.title', content: title});
  }

  ngOnInit() {
    this.getData();

    this.assistantUnregister = this.assistantActions.register('open_order', (action) => {
      const id = Number(action.order_id);

      if (!Number.isInteger(id) || id <= 0) {
        return;
      }

      this.assistantShow = {id, expand: action.expand !== false, cleared: false};
      this.highlight.set([id]);
      this.applyAssistantShow();
    });
  }

  getData() {
    // Новий запит робить попередній застарілим: його завершення вже не розкриває замовлення помічника
    const seq = ++this.dataSeq;
    this.dataLoading = true;

    if (this.dataSub) {
      this.dataSub.unsubscribe();
    }

    this.dataSub = this.http.post(this.apiRoot + 'orders/data', {
      search: this.search,
    })
      .pipe(takeUntil(this.isDestroyed), finalize(() => this.onListLoaded(seq)))
      .subscribe((data: OrdersData) => {
        this.allow_to_return_goods_from_zakupolog = data.allow_to_return_goods_from_zakupolog;
        this.orders = data.orders;
        this.currencies = data.currencies;
        this.returnStatuses = Object.values(data.return_statuses).filter(el => el.user_allowed === 1);

        this.dels = data.dels;

        if (this.orders) {
          for (let order of this.orders) {
            order.date = moment(order.date).format('DD.MM.YYYY HH:mm:ss')

            if (order.date === 'Invalid date') {
              order.date = '';
            }

            order.summa = order.summa * this.currencies[3].rate * 100 / 100;
            order.datedelivery = moment(order.datedelivery).format('DD.MM.YYYY');

            if (order.delivery_hour && order.delivery_hour !== '00:00') {
              order.deliveryHours = String(order.delivery_hour);
            } else {
              order.deliveryHours = '';
            }
          }
        }
      });
  };

  searchData() {
    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      this.getData();
    }, 500);
  }

  crossClears() {
    clearTimeout(this.timer);
    this.search = '';
    this.getData();
  }

  showOrder(order: Orders) {
    this.selectedOrder = order;

    this.modalPartnersNotReturnOrdersMessageComponentRef.show();
  }

  agree() {
    if (this.selectedOrder) {
      this.modalSelectGoodsReturnComponentRef.modalOrderShow(this.selectedOrder);
    }
  }

  showTrackStatus(order) {
    this.trackStatusOrder = order;

    this.http.post(this.apiRoot + 'orders_history/show_track_status', {
      // orders/data віддає номер замовлення як id (order_id у рядку немає) - уходив undefined
      order_id: order.id
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((data: ShowTrackStatus) => {
        this.trackStatusOrderProductsWars = data.order_products_wars;
        this.trackStatusTransfer = data.transfer;
        this.trackStatusContactsDrivers = data.contacts_drivers;

        this.isTrackStatusModalShow = true;
        this.isDarkBackground = true;
      });
  }

  closeModal() {
    this.changeButton = false;
    this.wrongComment = false;
    this.wrongQuantityGood = false;
    this.isShowOrderModalShow = false;
    this.isTrackStatusModalShow = false;
    this.isFormationRequestReturn = false;
    this.isOrderModalShow = false;
    this.isModalUserInfo = false;
    this.isDarkBackground = false;
  }

  /** Список завантажено (чи запит упав): показати те, що просив помічник */
  private onListLoaded(seq: number) {
    if (seq !== this.dataSeq) {
      return;
    }

    this.dataLoading = false;
    this.applyAssistantShow();
  }

  /**
   * Показати замовлення помічника, коли список готовий: підсвітити рядок, прокрутити до нього й (expand) розкрити - як клік
   * по замовленню (попередження про партнерські позиції, потім вибір товарів для повернення). Замовлення немає в списку, бо
   * його відсіяв пошук, - один раз знімаємо пошук і перечитуємо. Бек віддає лише замовлення з цього списку (останні 16 днів).
   */
  private applyAssistantShow() {
    const show = this.assistantShow;

    if (!show || this.dataLoading) {
      return;
    }

    const row = (this.orders || []).find(order => Number(order.id) === show.id);

    if (!row && this.search !== '' && !show.cleared) {
      show.cleared = true;
      clearTimeout(this.timer);
      this.search = '';
      this.getData();
      return;
    }

    this.assistantShow = null;

    if (row && show.expand) {
      this.showOrder(row);
    }

    this.highlight.ready();
  }

  ngOnDestroy() {
    // Завершення запиту списку (finalize) після знищення вже нічого не розкриває
    this.assistantUnregister?.();
    this.assistantShow = null;
    this.highlight.destroy();
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
